import { DownloadOutlined, FileAddOutlined, FileTextOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Modal, Space, Table, Typography, Upload, message } from "antd";
import type { UploadProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type CsvImportModalButtonProps = {
  title: string;
  templateUrl: string;
  templateFileName: string;
  guideUrl: string;
  buttonText?: string;
  guideButtonText?: string;
  uploadRequest: NonNullable<UploadProps["customRequest"]>;
  disabled?: boolean;
  uploading?: boolean;
  className?: string;
  buttonClassName?: string;
};

export function CsvImportModalButton({
  title,
  templateUrl,
  templateFileName,
  guideUrl,
  buttonText,
  guideButtonText,
  uploadRequest,
  disabled = false,
  uploading = false,
  className,
  buttonClassName,
}: CsvImportModalButtonProps) {
  const { i18n, t } = useTranslation();
  const resolvedButtonText = buttonText ?? t("csvImportModal.defaultButton");
  const resolvedGuideButtonText = guideButtonText ?? t("csvImportModal.guideButton");
  const guideLanguage = i18n.language.startsWith("en") ? "en" : "ja";
  const localizedGuideUrl = useMemo(() => {
    if (!guideUrl.includes("/csv-guides/")) {
      return guideUrl;
    }

    return guideUrl.replace("/csv-guides/", `/csv-guides/${guideLanguage}/`);
  }, [guideLanguage, guideUrl]);
  const [isOpen, setIsOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [guideContent, setGuideContent] = useState("");
  const [guideContentUrl, setGuideContentUrl] = useState("");
  const [isLoadingGuide, setIsLoadingGuide] = useState(false);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (!uploading) {
      setIsOpen(false);
    }
  }, [uploading]);

  const openGuide = useCallback(async () => {
    setIsGuideOpen(true);
    if (guideContent !== "" && guideContentUrl === localizedGuideUrl) {
      return;
    }

    setIsLoadingGuide(true);
    setGuideContent("");
    setGuideContentUrl("");
    try {
      const response = await fetch(localizedGuideUrl);
      if (!response.ok) {
        throw new Error(t("csvImportModal.guideFetchError"));
      }
      setGuideContent(await response.text());
      setGuideContentUrl(localizedGuideUrl);
    } catch (error) {
      console.error(t("csvImportModal.guideFetchError"), error);
      message.error(t("csvImportModal.guideFetchFailed"));
    } finally {
      setIsLoadingGuide(false);
    }
  }, [guideContent, guideContentUrl, localizedGuideUrl, t]);

  const closeGuide = useCallback(() => {
    setIsGuideOpen(false);
  }, []);

  const renderGuideContent = useCallback(() => {
    if (guideContent === "") {
      return null;
    }

    const elements: JSX.Element[] = [];
    const lines = guideContent.split(/\r?\n/);
    let index = 0;

    while (index < lines.length) {
      const line = lines[index];
      const key = `${index}-${line}`;

      if (line.trim() === "") {
        index += 1;
        continue;
      }

      if (line.startsWith("```")) {
        const codeLines: string[] = [];
        index += 1;
        while (index < lines.length && !lines[index].startsWith("```")) {
          codeLines.push(lines[index]);
          index += 1;
        }
        elements.push(
          <pre key={key} className="rounded bg-gray-100 p-3 text-sm overflow-x-auto">
            <code>{codeLines.join("\n")}</code>
          </pre>
        );
        index += 1;
        continue;
      }

      if (line.startsWith("|")) {
        const tableLines: string[] = [];
        while (index < lines.length && lines[index].startsWith("|")) {
          tableLines.push(lines[index]);
          index += 1;
        }
        const rows = tableLines
          .filter((tableLine) => !/^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(tableLine))
          .map((tableLine) => tableLine.split("|").slice(1, -1).map((cell) => cell.trim()));
        const header = rows[0] ?? [];
        const body = rows.slice(1).map((row, rowIndex) => ({ key: rowIndex, row }));
        elements.push(
          <Table
            key={key}
            size="small"
            pagination={false}
            className="my-3"
            columns={header.map((title, columnIndex) => ({
              title,
              dataIndex: ["row", columnIndex],
              key: `${columnIndex}-${title}`,
            }))}
            dataSource={body}
          />
        );
        continue;
      }

      if (line.startsWith("# ")) {
        elements.push(<Typography.Title key={key} level={4}>{line.replace(/^#\s+/, "")}</Typography.Title>);
      } else if (line.startsWith("## ")) {
        elements.push(<Typography.Title key={key} level={5}>{line.replace(/^##\s+/, "")}</Typography.Title>);
      } else if (line.startsWith("- ")) {
        const items: string[] = [];
        while (index < lines.length && lines[index].startsWith("- ")) {
          items.push(lines[index].replace(/^-\s+/, ""));
          index += 1;
        }
        elements.push(
          <ul key={key} className="list-disc pl-6">
            {items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        );
        continue;
      } else {
        elements.push(<Typography.Paragraph key={key}>{line}</Typography.Paragraph>);
      }

      index += 1;
    }

    return elements;
  }, [guideContent]);

  const handleUploadRequest = useCallback<NonNullable<UploadProps["customRequest"]>>((options) => {
    uploadRequest({
      ...options,
      onSuccess: (body, xhr) => {
        options.onSuccess?.(body, xhr);
        setIsOpen(false);
      },
    });
  }, [uploadRequest]);

  return (
    <span className={className}>
      <Button type="primary" icon={<FileAddOutlined />} onClick={openModal} disabled={disabled} className={buttonClassName}>
        {resolvedButtonText}
      </Button>
      <Modal
        title={title}
        open={isOpen}
        onCancel={closeModal}
        footer={null}
        centered
        destroyOnClose
      >
        <Space direction="vertical" size={18} className="w-full">
          <Typography.Text>
            {t("csvImportModal.description")}
          </Typography.Text>
          <div className="-mt-2 flex w-full justify-end">
            <Space direction="vertical" size={2} align="end">
              <Typography.Link href={templateUrl} download={templateFileName} className="inline-flex items-center gap-1 text-xs leading-5">
                <DownloadOutlined />
                {t("csvImportModal.templateDownload")}
              </Typography.Link>
              <Typography.Link onClick={openGuide} className="inline-flex items-center gap-1 text-xs leading-5">
                <FileTextOutlined />
                {resolvedGuideButtonText}
              </Typography.Link>
            </Space>
          </div>
          <div className="pt-1">
            <Upload
              customRequest={handleUploadRequest}
              maxCount={1}
              accept=".csv"
              showUploadList={false}
              disabled={disabled || uploading}
            >
              <Button type="primary" icon={<UploadOutlined />} loading={uploading} disabled={disabled || uploading}>
                {t("csvImportModal.selectAndImport")}
              </Button>
            </Upload>
          </div>
        </Space>
      </Modal>
      <Modal
        title={t("csvImportModal.guideTitle")}
        open={isGuideOpen}
        onCancel={closeGuide}
        footer={null}
        width={840}
        zIndex={1100}
        centered
        loading={isLoadingGuide}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          {renderGuideContent()}
        </div>
      </Modal>
    </span>
  );
}
